import React, { useRef, useContext, useState } from 'react';
import * as Yup from 'yup';
import {
  DetailContext,
  SnackbarContext,
  AbortableFetch,
  UserContext,
  useEventCallback,
  DetailHandle,
  abortableFetch,
  PromptContext,
  usePrompts,
  FormTextField,
  FormTextArea,
  FormAutocomplete,
  FormDateField,
  Checkbox,
  FormCustomField,
  FormContext,
} from '@eas/common-web';
import {
  Me,
  BoilerManufacturer,
  DictionaryBoilerBrand,
  DictionaryBoilerTypeAutocomplete,
} from '../../../models';
import { Messages, EvidenceAPI, Permission } from '../../../enums';
import { unstable_batchedUpdates } from 'react-dom';
import { useDictionaryBoilerTypes } from '../../dict-boiler-type/dict-boiler-types-api';
import { HelpLabel } from '../../../components/help/help-label';
import { getErrorMessage } from '../../../utils/get-message';

/**
 * Api call
 *
 * @param brand
 */
export function callAddApi(brand: DictionaryBoilerBrand) {
  return abortableFetch(`${EvidenceAPI.DICTIONARY_BOILER_BRANDS}/`, {
    method: 'POST',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(brand),
  });
}

export function callUpdateApi(brand: DictionaryBoilerBrand) {
  return abortableFetch(`${EvidenceAPI.DICTIONARY_BOILER_BRANDS}/${brand.id}`, {
    method: 'PUT',
    headers: new Headers({
      'Content-Type': 'application/json',
    }),
    body: JSON.stringify(brand),
  });
}

/**
 * Přidání značky
 */
const PROMPT_KEY = 'ADD_BRAND';

export function useAddBrandDialog({
  boilerBrands,
  selectedIndex,
  inDetailToolbar = false,
}: {
  boilerBrands?: DictionaryBoilerBrand[];
  selectedIndex: number | undefined;
  inDetailToolbar?: boolean;
}) {
  /**
   * Context stuff.
   */
  const { onPersisted, source } = useContext<DetailHandle<BoilerManufacturer>>(
    DetailContext
  );
  const { showSnackbar } = useContext(SnackbarContext);
  const { testPrompt } = useContext(PromptContext);
  const { hasPermission, reload } = useContext<UserContext<Me>>(UserContext);

  /**
   * Fetch ref.
   */
  const fetch = useRef<AbortableFetch | null>(null);

  /**
   * Dialog.
   */
  usePrompts(
    [
      {
        key: `${PROMPT_KEY}${inDetailToolbar ? '_IN_TOOLBAR' : ''}`,
        dialogTitle:
          selectedIndex !== undefined
            ? 'Editace značky'
            : 'Vytvoření značky k výrobci',
        dialogText: 'Vyplňte údaje:',
        formInitialValues:
          selectedIndex !== undefined ? boilerBrands?.[selectedIndex] : {},
        FormFields: function Body() {
          const { setFieldValue } = useContext(FormContext);
          const boilerTypes = useDictionaryBoilerTypes();
          const [withoutEnd, setWithoutEnd] = useState(false);
          const toggleWithoutEnd = useEventCallback(() => {
            setFieldValue('validTo', undefined);
            setWithoutEnd((current) => !current);
          });
          return (
            <>
              <FormTextField
                name="name"
                label={
                  <HelpLabel
                    label="Název"
                    code="BOILER_MANUFACTURER_ADD_BRAND_NAME"
                    required
                  />
                }
              />
              <FormAutocomplete
                name="allowedTypes"
                label={
                  <HelpLabel
                    label="Povolené typy kotle"
                    code="BOILER_MANUFACTURER_ADD_BRAND_ALLOWED_TYPES"
                    required
                  />
                }
                source={boilerTypes}
                multiple={true}
              />
              <FormDateField
                name="validFrom"
                label={
                  <HelpLabel
                    label="Platnost od"
                    code="BOILER_MANUFACTURER_ADD_BRAND_VALID_FROM"
                  />
                }
                representation="instant"
              />
              <FormDateField
                name="validTo"
                label={
                  <HelpLabel
                    label="Platnost do"
                    code="BOILER_MANUFACTURER_ADD_BRAND_VALID_TO"
                  />
                }
                disabled={withoutEnd}
                representation="instant"
              />
              <FormCustomField
                label={
                  <HelpLabel
                    label="Na neurčito"
                    code="BOILER_MANUFACTURER_ADD_BRAND_WITHOUT_END"
                  />
                }
              >
                <Checkbox onChange={toggleWithoutEnd} value={withoutEnd} />
              </FormCustomField>
              <FormTextArea
                name="description"
                label={
                  <HelpLabel
                    label="Popis"
                    code="BOILER_MANUFACTURER_ADD_BRAND_DESCRIPTION"
                  />
                }
              />
            </>
          );
        },
        formValidationSchema: Yup.object().shape({
          name: Yup.string().nullable().required('Povinné pole'),
          allowedTypes: Yup.array<DictionaryBoilerTypeAutocomplete>()
            .nullable()
            .min(1, 'Vyberte alespoň 1 typ kotle')
            .required('Povinné pole'),
        }),
        dialogWidth: 600,
      },
    ],
    [source.data, selectedIndex, boilerBrands]
  );

  const handleAddBrand = useEventCallback(async () => {
    testPrompt({
      key: `${PROMPT_KEY}${inDetailToolbar ? '_IN_TOOLBAR' : ''}`,
      submitCallback: async (brand: DictionaryBoilerBrand) => {
        try {
          source.setLoading(true);

          if (fetch.current !== null) {
            fetch.current.abort();
          }

          const manufacturerId = source?.data?.id;

          if (!manufacturerId) {
            return;
          }

          const completedBrand = {
            manufacturer: { id: manufacturerId },
            ...brand,
          };

          fetch.current =
            selectedIndex === undefined
              ? callAddApi(completedBrand)
              : callUpdateApi(completedBrand);

          await fetch.current.raw();

          unstable_batchedUpdates(() => {
            showSnackbar(...Messages.Ozo.ADD_BRAND.SUCCESS);
            source.setLoading(false);
          });

          reload();
          onPersisted(source.data!.id);
          await source.refresh();
        } catch (err) {
          source.setLoading(false);

          if (err.name !== 'AbortError') {
            const message = getErrorMessage(Messages.Ozo.ADD_BRAND, err.code);

            showSnackbar(...message);

            throw err;
          }
          return undefined;
        }
      },
    });
  });

  const canAddBrand = hasPermission(Permission.Ozo.OZO_BRAND_UPDATE, {
    searchInSubjectPerms: true,
  });

  const canEditBrand =
    canAddBrand &&
    selectedIndex !== undefined &&
    boilerBrands?.[selectedIndex]?.active === true;

  return {
    handleAddBrand,
    canAddBrand,
    canEditBrand,
  };
}
